import { Controller } from "@hotwired/stimulus";
import "./component.scss";

export default class extends Controller {
  static targets = ["audio", "shell", "currentTime", "duration", "playIcon", "pauseIcon", "progress"];
  static values = { initialDuration: Number, mediaTitle: String, artworkUrl: String };

  connect() {
    if (this.hasAudioTarget) {
      this.audioTarget.removeAttribute("controls");
      this.audioTarget.classList.remove(...this.audioTarget.classList);
    }
    if (this.hasShellTarget) {
      this.shellTarget.hidden = false;
    }

    this.syncMetadata();
    this.syncProgress();
    this.handlePause();
  }

  disconnect() {
    if (this.hasAudioTarget && !this.audioTarget.paused) {
      this.audioTarget.pause();
    }
  }

  toggle() {
    if (!this.hasAudioTarget) return;

    if (this.audioTarget.paused) {
      const playPromise = this.audioTarget.play();
      playPromise?.catch(() => {});
      return;
    }

    this.audioTarget.pause();
  }

  seek(event) {
    if (!this.hasAudioTarget) return;

    const nextTime = Number(event.target.value);
    if (!Number.isFinite(nextTime)) return;

    this.audioTarget.currentTime = nextTime;
    this.syncProgress();
  }


  syncMetadata() {
    const duration = this.effectiveDuration();

    if (this.hasProgressTarget) {
      this.progressTarget.max = Math.max(duration, 1);
    }

    if (this.hasDurationTarget) {
      this.durationTarget.textContent = this.formatTime(duration);
    }

    this.paintProgress(this.audioTarget?.currentTime || 0, duration);
  }

  syncProgress() {
    if (!this.hasAudioTarget) return;

    const currentTime = this.audioTarget.currentTime || 0;
    const duration = this.effectiveDuration();

    if (this.hasCurrentTimeTarget) {
      this.currentTimeTarget.textContent = this.formatTime(currentTime);
    }

    if (this.hasProgressTarget) {
      this.progressTarget.value = currentTime;
    }

    this.paintProgress(currentTime, duration);
  }

  handlePlay() {
    this.toggleIcons(true);
    this.setMediaSession();
  }

  handlePause() {
    this.toggleIcons(false);
  }

  handleEnded() {
    if (!this.hasAudioTarget) return;

    this.audioTarget.currentTime = 0;
    this.syncProgress();
    this.handlePause();
  }

  setMediaSession() {
    if (!("mediaSession" in navigator)) return;

    const artwork = [];
    if (this.artworkUrlValue) {
      artwork.push({ src: this.artworkUrlValue });
    }

    navigator.mediaSession.metadata = new MediaMetadata({
      title: this.mediaTitleValue || "Audio",
      artwork,
    });
  }

  effectiveDuration() {
    const browserDuration = this.hasAudioTarget ? this.audioTarget.duration : 0;
    if (Number.isFinite(browserDuration) && browserDuration > 0) {
      return browserDuration;
    }

    return this.initialDurationValue > 0 ? this.initialDurationValue : 0;
  }

  toggleIcons(isPlaying) {
    if (this.hasPlayIconTarget) {
      this.playIconTarget.hidden = isPlaying;
    }

    if (this.hasPauseIconTarget) {
      this.pauseIconTarget.hidden = !isPlaying;
    }
  }

  paintProgress(currentTime, duration) {
    if (!this.hasProgressTarget) return;

    const safeDuration = duration > 0 ? duration : 1;
    const ratio = Math.max(0, Math.min(currentTime / safeDuration, 1));
    this.progressTarget.style.setProperty("--progress", `${ratio * 100}%`);
  }

  formatTime(rawSeconds) {
    if (!Number.isFinite(rawSeconds) || rawSeconds <= 0) {
      return "00:00";
    }

    const totalSeconds = Math.floor(rawSeconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return [hours, minutes, seconds].map((part) => String(part).padStart(2, "0")).join(":");
    }

    return [minutes, seconds].map((part) => String(part).padStart(2, "0")).join(":");
  }
}
